import { Link } from "react-router-dom";
import { GraduationCap, BookMarked, ArrowLeft, LogOut, ShieldCheck, Megaphone } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { SCHOOL_LOGO, SCHOOL_NAME, SCHOOL_SUB, ROLES } from "../lib/constants";

export default function Portal() {
  const { user, logout } = useAuth();

  return (
    <div dir="rtl" className="min-h-screen bg-gradient-to-b from-[#EAF6F4] via-[#F6FAF9] to-white relative overflow-hidden">
      {/* Ambient decoration */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-teal-200/30 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-amber-200/30 blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-teal-100/60 bg-white/70 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-teal-50 ring-1 ring-teal-200/60 flex items-center justify-center overflow-hidden">
              <img src={SCHOOL_LOGO} alt="شعار المدرسة" className="w-9 h-9 object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-cairo font-extrabold text-teal-800 text-[15px] sm:text-base">{SCHOOL_NAME}</div>
              <div className="text-[11px] text-slate-500">{SCHOOL_SUB}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user && (
              <>
                <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-teal-700 text-white text-[11px] font-cairo">
                  <ShieldCheck size={12} /> {ROLES[user.role] || user.role}
                </span>
                <button
                  onClick={logout}
                  data-testid="portal-logout"
                  className="inline-flex items-center gap-1.5 text-slate-600 hover:text-rose-600 text-sm font-cairo px-3 py-1.5 rounded-md hover:bg-rose-50 transition-colors"
                >
                  <LogOut size={14} /> خروج
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-3xl mx-auto"
        >
          {user && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-teal-200/60 text-teal-700 text-xs font-cairo mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
              مرحباً {user.full_name || user.username}
            </div>
          )}
          <h1 className="font-cairo text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 leading-tight tracking-tight">
            بوابة الأنظمة الإلكترونية
          </h1>
          <div className="mx-auto mt-3 h-[3px] w-20 rounded-full bg-gradient-to-l from-amber-400 to-transparent" />
          <p className="mt-4 text-slate-600 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            اختر النظام الذي تود الدخول إليه — كل نظام يعمل باستقلالية كاملة ضمن مظلة واحدة.
          </p>
        </motion.div>

        {/* Two cards */}
        <div className="mt-10 sm:mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-7">
          <PortalCard
            to="/login"
            testid="portal-card-students"
            accent="teal"
            icon={GraduationCap}
            title="نظام إدارة الطلاب"
            desc="لوحة تحكم كاملة لإدارة الطلاب، الغياب، الخطط الدراسية، التقارير وإشعارات أولياء الأمور."
            tags={["الطلاب", "الغياب", "الخطط", "التقارير", "واتساب"]}
            cta="تسجيل الدخول"
            delay={0.05}
          />
          <PortalCard
            to="/library"
            testid="portal-card-library"
            accent="gold"
            icon={BookMarked}
            title="المكتبة الرقمية"
            desc="أرشيف مؤسسي احترافي للوثائق التعليمية بتنظيم هرمي: المرحلة ← المجال ← المؤشر ← الملف."
            tags={["الوثائق", "الأرشفة", "PDF", "المؤشرات"]}
            cta="الدخول للمكتبة"
            delay={0.15}
          />
          <PortalCard
            to="/pickup/login"
            testid="portal-card-pickup"
            accent="blue"
            icon={Megaphone}
            title="نظام استدعاء الطالب"
            desc="نظام سريع وآمن لأولياء الأمور لاستدعاء أبنائهم أثناء اليوم الدراسي، مع تتبع لحظي لحالة الطلب."
            tags={["أولياء الأمور", "استدعاء فوري", "تتبع الحالة"]}
            cta="دخول أولياء الأمور"
            delay={0.25}
          />
        </div>

        <div className="mt-6 text-center text-[12px] text-slate-500 font-cairo">
          {new Date().getFullYear()} © {SCHOOL_NAME} — <span className="text-teal-800 font-bold">تصميم وبرمجة الأستاذ / محمد كامل</span>
          <span className="mx-1">•</span>
          <span dir="ltr" className="tabular-nums text-teal-800 font-bold">0590939015</span>
        </div>
      </main>
    </div>
  );
}

const ACCENTS = {
  teal: {
    bar: "bg-gradient-to-l from-teal-700 via-teal-500 to-teal-700",
    icon: "bg-teal-50 text-teal-700 ring-teal-200/70",
    tag: "bg-teal-50 text-teal-700 border border-teal-100",
    cta: "bg-teal-700 text-white group-hover:bg-teal-800",
  },
  gold: {
    bar: "bg-gradient-to-l from-amber-600 via-amber-400 to-amber-600",
    icon: "bg-amber-50 text-amber-700 ring-amber-200/70",
    tag: "bg-amber-50 text-amber-800 border border-amber-100",
    cta: "bg-amber-500 text-white group-hover:bg-amber-600",
  },
  blue: {
    bar: "bg-gradient-to-l from-sky-700 via-sky-500 to-sky-700",
    icon: "bg-sky-50 text-sky-700 ring-sky-200/70",
    tag: "bg-sky-50 text-sky-700 border border-sky-100",
    cta: "bg-sky-600 text-white group-hover:bg-sky-700",
  },
};

function PortalCard({ to, testid, accent, icon: Icon, title, desc, tags, cta, delay }) {
  const c = ACCENTS[accent] || ACCENTS.teal;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={to}
        data-testid={testid}
        className={`group relative block rounded-3xl overflow-hidden bg-white ring-1 ring-slate-200 shadow-[0_10px_30px_-15px_rgba(15,118,110,0.25)] hover:shadow-[0_18px_45px_-15px_rgba(15,118,110,0.35)] transition-all duration-500 hover:-translate-y-1`}
      >
        {/* Top accent bar */}
        <div className={`h-1.5 w-full ${c.bar}`} />

        <div className="p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className={`shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center ring-1 ${c.icon} group-hover:scale-105 transition-transform`}>
              <Icon size={28} strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-cairo font-extrabold text-slate-900 text-xl sm:text-2xl tracking-tight">{title}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{desc}</p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span key={t} className={`text-[11px] px-2 py-0.5 rounded-full ${c.tag}`}>
                {t}
              </span>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <span className={`inline-flex items-center gap-2 font-cairo font-bold text-sm px-4 py-2 rounded-xl transition-colors ${c.cta}`}>
              {cta}
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            </span>
            <span className="text-[11px] font-cairo text-slate-400">مستقل تماماً</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
